/**
 * MST Wishlist Prime -> theme bridges.
 *
 * Three blocks live here, all gluing the app's injected UI to the theme:
 *   0. product-page button config  -> supply the icon markup the app needs to wire the PDP heart
 *   1. wishlist drawer add-to-cart -> refresh the cart drawer and header cart count
 *   2. wishlist count              -> hide the header wishlist badge when the count is zero
 *
 * ---------------------------------------------------------------------------------------------
 * 0. Product-page button config.
 *
 * The app only wires the product-page hook (`.iWishAdd`) if BOTH of these hold, read from its own
 * source (`v3-iwishlist.js`):
 *
 *     if (document.querySelectorAll(".iWishAdd").length &&
 *         typeof window.iWishData.iwish_add_txt !== 'undefined') { ... }
 *
 * and inside that branch it fills an empty hook with `iwish_add_txt`, swapping to `iwish_added_txt`
 * once the product is saved. Those two keys are defined by the app's PRODUCT app block — and this
 * store has that block on `templates/collection.json` only, never on `templates/product.json`. So
 * on a product page the keys are absent, the branch never runs, and the hook stays empty and
 * unclickable no matter how it is styled. Verified 2026-08-10: product page had 53 `iWishData`
 * keys and none of the four `*_txt` ones; the collection page had 61 and all four, and only there
 * did the hooks carry the app's `data-iwclick`.
 *
 * Defining the two keys here is the theme-side alternative to adding that app block. It is
 * deliberate, for three reasons:
 *   - the block lives in `templates/product.json`, which is merchant-owned content and cannot be
 *     changed from `dev` (it would not survive the golden-rule merge to `main` anyway);
 *   - the block also renders the app's OWN button, which would then have to be hidden to keep the
 *     single square control the design calls for;
 *   - the app's own markup pairs the icon with a French text label, which a square icon-only box
 *     would have to hide regardless.
 *
 * So we supply the icon and let the app own everything else — binding, storage, the added/removed
 * state swap, the counter. The hearts below deliberately mirror `snippets/icon.liquid`'s `heart`
 * used in the header: outline when not saved, filled when saved, `currentColor` throughout so the
 * box's colour drives them.
 *
 * DEPENDENCY, stated plainly: `iwish_add_txt` / `iwish_added_txt` are the app's key names, read out
 * of its source rather than documented by the vendor. If the app renames them the PDP heart goes
 * back to being an empty box — it fails to nothing, it does not break the page. Adding the app's
 * product block to the product template is the vendor-sanctioned alternative if that ever happens.
 *
 * Load order matters and is deterministic, not lucky: this asset's <script defer> sits in
 * `layout/theme.liquid` at line ~90, ahead of the app's own deferred scripts in the document, and
 * deferred scripts execute in document order — so these keys are set before the app initialises.
 */
(function () {
  var HEART =
    '<svg viewBox="0 0 256 256" width="20" height="20" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M128,216S28,160,28,92A52,52,0,0,1,128,72h0A52,52,0,0,1,228,92C228,160,128,216,128,216Z" ' +
    'fill="FILL" stroke="currentColor" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  window.iWishData = window.iWishData || {};

  if (typeof window.iWishData.iwish_add_txt === 'undefined') {
    window.iWishData.iwish_add_txt = HEART.replace('FILL', 'none');
  }

  if (typeof window.iWishData.iwish_added_txt === 'undefined') {
    window.iWishData.iwish_added_txt = HEART.replace('FILL', 'currentColor');
  }
})();

/**
 * ---------------------------------------------------------------------------------------------
 * 1. Wishlist drawer -> cart bridge.
 *
 * When a product is added to the cart from inside the wishlist drawer, the app does not tell the
 * theme. Without this the cart drawer and the header count both stay stale until the next page
 * load. This listener refreshes both, and closes the wishlist drawer when the add came from the
 * paginated list.
 *
 * Ported from the inherited 5.4.1 theme, where it was appended to `assets/theme.js.liquid`
 * (lines 4314-4361). Impact 7.2.0 ships a plain, Liquid-free `assets/theme.js` that is vendor
 * code, so this lives in its own asset instead of contaminating the vendor bundle.
 *
 * The 1000ms delay is the inherited behaviour: it waits for the app's own /cart/add round-trip to
 * land before re-reading the cart. It is a race, not a guarantee — if the cart count is ever seen
 * lagging after a wishlist add, this timeout is the first suspect.
 */
document.addEventListener('DOMContentLoaded', function () {
  document.body.addEventListener('click', function (e) {
    // Case 1: the standard add-to-cart button inside the wishlist drawer.
    const btnWishlist = e.target.closest('#iwish-drawer-root .add_to_cart');
    // Case 2: the add-to-cart button inside the drawer's paginated list.
    const btnPagination = e.target.closest('#iwish-drawer-root .paginationContainer .addToCart-btn');

    if (!btnWishlist && !btnPagination) return;

    setTimeout(function () {
      // Refresh the cart drawer.
      if (window.CartDrawer && typeof window.CartDrawer.refresh === 'function') {
        window.CartDrawer.refresh();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      // Update the header cart count.
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          const countBubble = document.querySelector('.header__cart-count cart-count');
          if (!countBubble) return;

          countBubble.textContent = cart.item_count;
          countBubble.setAttribute('aria-hidden', cart.item_count > 0 ? 'false' : 'true');
          countBubble.style.opacity = cart.item_count > 0 ? '1' : '0';
        });

      // Adding from the paginated list closes the wishlist drawer.
      if (btnPagination) {
        const wishlistRoot = document.querySelector('#iwish-drawer-root');
        if (wishlistRoot) {
          wishlistRoot.style.display = 'none';
        }
      }
    }, 1000);
  });
});

/**
 * ---------------------------------------------------------------------------------------------
 * 2. Wishlist count -> header badge visibility.
 *
 * The header wishlist heart (`snippets/wishlist-app-links.liquid`) reuses the theme's
 * `count-bubble`, so its badge must behave like the cart's: the cart bubble carries `opacity-0`
 * from Liquid when the cart is empty, and the bridge above sets the same opacity after an add.
 * The wishlist badge cannot do that server-side — the count lives in the app, not in Liquid, so
 * the markup always ships "0" and the app overwrites it client-side. Left alone the header shows
 * a filled "0" bubble on the heart while the cart beside it shows none.
 *
 * VERIFIED 2026-08-10: the app updates our badge by writing textContent, and does NOT replace the
 * element or strip its classes — so observing the node is safe and the class survives. It also
 * does not stamp the badge with any attribute of its own, which is why there is no app-side hook
 * to listen to and a MutationObserver is the mechanism.
 */
document.addEventListener('DOMContentLoaded', function () {
  const badge = document.querySelector('.iwish-counter.count-bubble');
  if (!badge) return;

  function syncBadge() {
    const count = parseInt(badge.textContent.trim(), 10);
    const hasItems = Number.isFinite(count) && count > 0;

    badge.style.opacity = hasItems ? '1' : '0';
    badge.setAttribute('aria-hidden', hasItems ? 'false' : 'true');
  }

  syncBadge();

  // The app writes the real count in asynchronously, after its own fetch resolves.
  new MutationObserver(syncBadge).observe(badge, {
    childList: true,
    characterData: true,
    subtree: true,
  });
});

/**
 * ---------------------------------------------------------------------------------------------
 * 3. Wishlist drawer "Continuer vos achats" -> the Nouveautés collection.
 *
 * The app points that button at `/collections/all`, which is a utility collection, not a shopping
 * destination — it is excluded from the Mechanic sales-sort for that reason. Sending a customer who
 * is done with their wishlist to the new season instead is the merchandising call (Michael,
 * 2026-08-10).
 *
 * It cannot be configured. `continue_shopping` in the app's settings is the LABEL only; the URL is
 * hardcoded in the app's React bundle (`v3.main.js`), in two places:
 *
 *     href: `${a}collections/all`   // empty-wishlist state, with a locale/root prefix
 *     href: "/collections/all"      // populated state, no prefix
 *
 * So it is rewritten after render. Both forms are handled by replacing only the trailing
 * `collections/all`, which preserves any root prefix the first form carries rather than assuming
 * the storefront is served from `/`.
 *
 * A MutationObserver rather than a one-off pass because the drawer is React-rendered: it is built
 * on first open and re-rendered whenever the list changes, and each re-render restores the app's
 * own href. The observer is idempotent — it only touches anchors still pointing at the old target,
 * so re-entry from its own mutations cannot loop.
 *
 * `/collections/nouveautes` verified to exist 2026-08-10 via the Admin API: "Nouveautés
 * Automne-Hiver 2026", 894 products. If it is ever renamed this silently leaves the app's original
 * link in place rather than producing a 404.
 */
document.addEventListener('DOMContentLoaded', function () {
  var OLD_SUFFIX = 'collections/all';
  var NEW_SUFFIX = 'collections/nouveautes';

  function retargetContinueShopping(root) {
    root.querySelectorAll('.continue-shopping-btn a').forEach(function (link) {
      var href = link.getAttribute('href');
      if (!href) return;

      // Trailing-segment match only, so a root/locale prefix on the empty-state variant survives.
      if (href.replace(/\/+$/, '').endsWith(OLD_SUFFIX)) {
        link.setAttribute('href', href.replace(/collections\/all\/?$/, NEW_SUFFIX));
      }
    });
  }

  var drawerRoot = document.querySelector('#iwish-drawer-root');
  if (!drawerRoot) return;

  retargetContinueShopping(drawerRoot);

  new MutationObserver(function () {
    retargetContinueShopping(drawerRoot);
  }).observe(drawerRoot, { childList: true, subtree: true });
});
